import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRecommendationEnginePipelineEditor } from "../../../hooks/useRecommendationEnginePipelineEditor";
import {
  hasInvalidPipelineRadius,
  RADIUS_KM_MAX,
  RECOMMENDATION_ENGINE_ENDPOINT_CONFIG_KEYS,
  RECOMMENDATION_ENGINE_ENDPOINT_LABELS,
} from "../../../utils/recommendationEngine/constants";
import { RecommendationEngineEndpoint } from "../../../types/recommendationEngine";
import RecommendationEnginePipelineEditor from "./RecommendationEnginePipelineEditor";
import PreviewModal from "./components/PreviewModal";
import HowThisWorksAccordion from "./components/HowThisWorksAccordion";
import InfoTooltip from "./components/InfoTooltip";
import { ENDPOINT_SUBTITLES, FIELD_TOOLTIPS } from "./copy";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";

type Props = {
  endpoint: RecommendationEngineEndpoint;
};

const configKeyForEndpoint = (endpoint: RecommendationEngineEndpoint) =>
  RECOMMENDATION_ENGINE_ENDPOINT_CONFIG_KEYS[endpoint];

const RecommendationEnginePipelineEditorPanel = ({ endpoint }: Props) => {
  const configKey = configKeyForEndpoint(endpoint);
  const endpointLabel = RECOMMENDATION_ENGINE_ENDPOINT_LABELS[endpoint];
  const {
    loadedVersion,
    draft,
    dirty,
    isLoading,
    isSaving,
    signals,
    candidateSources: candidateSourceCatalog,
    loadError,
    updateDraft,
    savePipeline,
    resetPipeline,
    discardChanges,
  } = useRecommendationEnginePipelineEditor(configKey, endpoint);

  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const hasRadiusErrors = hasInvalidPipelineRadius(draft);

  const handleSave = async () => {
    const result = await savePipeline(
      `${endpointLabel} updated from the admin recommendation engine editor`,
    );
    if (result.success) {
      toastSuccess("Recommendation engine configuration saved.");
      return;
    }
    const detail = result.errors?.join("\n") ?? result.message;
    if (detail?.toLowerCase().includes("version")) {
      toastError(`${detail} Reload and try again.`);
    } else {
      toastError(detail ?? "Save failed.");
    }
  };

  const handleReset = async () => {
    const result = await resetPipeline();
    if (result.success) {
      toastSuccess(
        "Recommendation engine configuration reset to the server default.",
      );
    } else {
      toastError(result.message ?? "Reset failed.");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          mb: 1,
        }}
      >
        <Chip label={endpointLabel} size="small" variant="outlined" />
        <Box sx={{ display: "inline-flex", alignItems: "center" }}>
          <Chip label={`v${loadedVersion}`} size="small" />
          <InfoTooltip title={FIELD_TOOLTIPS.version} />
        </Box>
        {dirty && <Chip label="Unsaved changes" color="warning" size="small" />}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={FIELD_TOOLTIPS.preview} placement="top" arrow>
          <Button size="small" onClick={() => setPreviewOpen(true)}>
            Preview
          </Button>
        </Tooltip>
        <Tooltip title={FIELD_TOOLTIPS.discardChanges} placement="top" arrow>
          <span>
            <Button
              size="small"
              onClick={discardChanges}
              disabled={!dirty || isSaving}
            >
              Discard
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={FIELD_TOOLTIPS.resetToDefault} placement="top" arrow>
          <span>
            <Button size="small" onClick={handleReset} disabled={isSaving}>
              Reset to default
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSave}
          disabled={isSaving || !dirty || hasRadiusErrors}
        >
          {isSaving ? <CircularProgress size={18} color="inherit" /> : "Save"}
        </Button>
      </Box>

      {hasRadiusErrors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Radius values must be greater than 0 and at most {RADIUS_KM_MAX} km.
          Fix the highlighted fields before saving.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {ENDPOINT_SUBTITLES[endpoint]}
      </Typography>

      <HowThisWorksAccordion endpoint={endpoint} />

      <RecommendationEnginePipelineEditor
        endpoint={endpoint}
        pipeline={draft}
        onChange={updateDraft}
        signals={signals}
        candidateSources={candidateSourceCatalog}
      />

      <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
        Test before saving — use the Preview button at the top of the page to
        try this configuration against a real source item or context without
        affecting the live recommendation engine.
      </Alert>

      <PreviewModal
        open={previewOpen}
        endpoint={endpoint}
        pipeline={draft}
        onClose={() => setPreviewOpen(false)}
      />
    </Box>
  );
};

export default RecommendationEnginePipelineEditorPanel;
